import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AwarenessModule } from '@modules/awareness/awareness.module';
import { TlkSlideToggleComponent } from '@itero/ui-toolkit-angular/slide-toggle';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseDestroyableDirective } from '@shared/base-classes/base-destroyable';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';
import { Observable, of } from 'rxjs';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { rxModeToggled } from '@core/services/logger/bi-actions';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { emptyGuid } from '@shared/models/consts';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { PatientStore } from '@modules/patient/state/patient-store';
import { PopupService } from '@shared/services/popup.service';

@Component({
	selector: 'rx-mode-toggle-banner',
	templateUrl: './rx-mode-toggle-banner.component.html',
	styleUrls: ['./rx-mode-toggle-banner.component.scss'],
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, TranslateModule, AwarenessModule, TlkSlideToggleComponent]
})
export class RxModeToggleBannerComponent extends BaseDestroyableDirective implements OnInit {
	modeControl: FormControl<boolean>;

	constructor(
		private readonly intuitiveFlowService: IntuitiveFlowService,
		private readonly biLogger: BiLoggerService,
		private readonly patientQuery: PatientQuery,
		private readonly patientStore: PatientStore,
		private readonly cdr: ChangeDetectorRef,
		private readonly popupService: PopupService,
	) {
		super();
	}

	ngOnInit() {
		this.modeControl = new FormControl<boolean>(!this.intuitiveFlowService.preferOldRxMode, { nonNullable: true });
		this.intuitiveFlowService.preferOldRxMode$
			.pipe(
				takeUntil(this.componentAlive$),
				map(preferOldRxmode => !preferOldRxmode),
				tap(checked => {
					if (this.modeControl.value !== checked) {
						this.modeControl.patchValue(checked, { emitEvent: false });
					}
				})
			)
			.subscribe();
		this.modeControl.valueChanges
			.pipe(
				takeUntil(this.componentAlive$),
				switchMap(checked => this.handleModeChanged(checked))
			)
			.subscribe();
	}

	handleModeChanged(checked: boolean) {
		return this.switchMode(checked);
	}

	private switchMode(switchToNew: boolean): Observable<PopUpActions> {
		const hasGuid = !!this.patientQuery.patientGuid && this.patientQuery.patientGuid !== emptyGuid;

		if (hasGuid) {
			this.setUserPreference(switchToNew);

			return of(PopUpActions.Ok);
		}

		const popUpInput = {
			titleTranslationKey: 'Popup.Confirmation',
			contentTranslationKey: 'PatientSection.ClearPatientConfirmationMessage'
		};

		return this.popupService.openConfirmationPopUp({ popUpInput }).pipe(
			tap(result => {
				if (result === PopUpActions.Ok) {
					this.patientStore.clearPatient();
					this.setUserPreference(switchToNew);
				} else {
					this.setToggleValue(!switchToNew);
				}
			})
		);
	}

	private setUserPreference(value: boolean) {
		void this.biLogger.reporter?.trackUserAction(rxModeToggled(value));
		this.intuitiveFlowService.setRxModePreference(value);
	}

	private setToggleValue(value: boolean) {
		this.modeControl.patchValue(value, { emitEvent: false });
		this.cdr.markForCheck();
	}
}
